import {
  capitalize,
  cloneDeep,
  isObjectLike,
} from 'lodash';
import * as homeConstants from '@/constants/products/home';
import * as commonConstants from '@/constants/common';

import {
  nameRegex,
} from '@/helpers/common';

import {
  descriptionRegex,
  calcContentsSum,
} from '@/helpers/products/home';

import {
  DATE_FORMAT,
  UtcDate,
} from '@/helpers/date';
import * as homePayloadTemplates from '@/payloadTemplates/home';
import { validate } from 'vee-validate';

const constants = Object.assign(
  {},
  commonConstants,
  homeConstants,
);

export const initHomeRules = (options) => (step = {}, data) => {
  const { propertySort } = step;

  return {
    // postcode/address
    postcode: {
      postcode: true,
    },
    houseNumberOrName: {
      required: true,
      typeof: 'string',
    },
    addressLine2: {
      required: true,
      typeof: 'string',
    },
    addressLine3: {
      required: true,
      typeof: 'string',
    },
    addressLine4: {
      required: true,
      typeof: 'string',
    },

    // property type
    propertyOwned: {
      required: true,
      in: {
        items: constants.HOME_PROPERTY_OWNED,
      },
    },
    propertyCoverType: {
      required: true,
      in: {
        items: constants.COVER_PROPERTY_COVER_TYPE,
      },
    },
    propertySort: {
      required: true,
      in: {
        items: constants.HOME_PROPERTY_SORT,
      },
    },
    propertyType: {
      required: true,
      in: {
        // flatten HOME_PROPERTY_TYPE since its deep object
        items: (() => {
          // eslint-disable-next-line no-prototype-builtins
          const propSort = propertySort && constants.HOME_PROPERTY_TYPE.hasOwnProperty(propertySort) ? propertySort : 'H';
          return Object.keys(constants.HOME_PROPERTY_TYPE[propSort]).map(type => `${propertySort}${type}`);
        })(),
      },
    },
    propertyFloor: {
      required: propertySort === 'A',
      in: {
        items: constants.HOME_PROPERTY_FLOOR,
      },
    },

    // property rooms
    // TODO add extra check for at least 1 room / not sure about required for every field
    propertyBedrooms: {
      required: true,
      between: [constants.PROPERTY_ROOMS_MIN_QTY, constants.PROPERTY_ROOMS_MAX_QTY],
    },
    propertyBathrooms: {
      required: true,
      between: [constants.PROPERTY_ROOMS_MIN_QTY, constants.PROPERTY_ROOMS_MAX_QTY],
    },
    propertyLivingrooms: {
      required: true,
      between: [constants.PROPERTY_ROOMS_MIN_QTY, constants.PROPERTY_ROOMS_MAX_QTY],
    },
    propertyOtherooms: {
      required: true,
      between: [constants.PROPERTY_ROOMS_MIN_QTY, constants.PROPERTY_ROOMS_MAX_QTY],
    },
    propertyKitchenrooms: {
      between: [constants.PROPERTY_ROOMS_MIN_QTY, constants.PROPERTY_ROOMS_MAX_QTY],
    },

    propertyYear: {
      required: true,
      propertyYear: true,
    },

    propertyRoofMade: {
      required: true,
      in: {
        items: constants.HOME_PROPERTY_ROOF_MADE,
      },
    },

    propertyFlatRoof: {
      required: true,
      in: {
        items: constants.HOME_PROPERTY_FLAT_ROOF,
      },
    },

    // property assumptions
    propertyWallsMade: {
      required: true,
      in: {
        items: constants.HOME_PROPERTY_WALLS_MADE,
      },
    },
    propertyLandslipHeave: {
      required: true,
      typeof: 'boolean',
    },
    propertyUnderpinning: {
      required: true,
      typeof: 'boolean',
    },
    propertyFlooding: {
      required: true,
      typeof: 'boolean',
    },
    propertyWork: {
      required: true,
      typeof: 'boolean',
    },
    propertyNearestRiver: {
      required: true,
      in: {
        items: constants.HOME_PROPERTY_NEAREST_RIVER,
      },
    },
    propertyListed: {
      required: true,
      in: {
        items: constants.HOME_PROPERTY_LISTED,
      },
    },
    propertyMainResidence: {
      required: true,
      in: {
        items: constants.HOME_PROPERTY_MAIN_RESIDENCE,
      },
    },
    propertyStateOfRepair: {
      required: true,
      typeof: 'boolean',
    },
    propertyNearestTree: {
      required: true,
      typeof: 'boolean',
    },

    buildSumsIns: {
      required: options.isBuildingCovered,
      switchedBy: {
        condition: options.isBuildingCovered,
      },
      between: [constants.PROPERTY_REBUILD_COST_MIN, constants.PROPERTY_REBUILD_COST_MAX],
    },
  };
};

export const initContentRules = (options) => (step = {}, data) => {
  return {
    propertyLaptopInsurance: {
      required: options.isContentsCovered,
      typeof: 'boolean',
      switchedBy: {
        condition: options.isContentsCovered,
      },
    },

    bicyclesOrLaptopsDetails: {
      required: options.isContentsCovered && step.propertyLaptopInsurance,
      switchedBy: {
        condition: options.isContentsCovered && step.propertyLaptopInsurance,
      },
    },

    propertyItemsInsurance: {
      required: options.isContentsCovered,
      typeof: 'boolean',
      switchedBy: {
        condition: options.isContentsCovered,
      },
    },

    specificItemsDetails: {
      required: options.isContentsCovered && step.propertyItemsInsurance,
      switchedBy: {
        condition: options.isContentsCovered && step.propertyItemsInsurance,
      },
    },

    allRisksUnspecified: {
      required: options.isContentsCovered,
      switchedBy: {
        condition: options.isContentsCovered,
      },
      in: {
        items: constants.COVER_ALL_RISKS_UNSPECIFIED,
      },
    },

    contentsSumsIns: (() => {
      if (!options.isContentsCovered) {
        return {
          required: false,
          switchedBy: {
            condition: options.isContentsCovered,
          },
        };
      }

      return {
        required: true,
        switchedBy: {
          condition: options.isContentsCovered,
        },
        contentsReplaceCost: { totalContentsCost: options.totalContentsCost },
      };
    })(),

    propertyPurchased: (() => {
      if (!options.isPropertyPurchased) {
        return {};
      }

      return {
        required: options.isBuildingCovered && options.isPropertyOwned,
        propertyPurchasedDate: options.isPropertyPurchased,
        date_format: DATE_FORMAT,
      };
    })(),

    propertyYearsLived: (() => {
      if (!options.isPropertyPurchased) {
        return {};
      }

      return {
        required: true,
        propertyYearsLived:
          { maxYears: options.propertyYearsLivedMax },
      };
    })(),

    propertyOccupied: {
      required: true,
      in: {
        items: constants.HOME_PROPERTY_OCCUPIED,
      },
    },

    propertyAdults: {
      required: true,
      between: [1, 6],
    },

    propertyChildren: {
      required: true,
      between: [0, 6],
    },

    propertySmoker: {
      required: true,
      typeof: 'boolean',
    },

    propertyUnoccupied: {
      required: true,
      in: {
        items: constants.HOME_PROPERTY_UNOCCUPIED,
      },
    },

    propertyUnoccupiedDays: {
      required: true,
      in: {
        items: constants.HOME_PROPERTY_UNOCCUPIED_DAYS,
      },
    },

    propertyBusinessType: {
      required: true,
      in: {
        items: constants.HOME_PROPERTY_BUSINESS_TYPE,
      },
    },

    propertyAlarmInstalled: {
      required: true,
      typeof: 'boolean',
    },

    propertyProfAlarm: {
      required: options.isPropertyAlarmInstalled,
      typeof: 'boolean',
    },

    propertyAlertAlarm: {
      required: options.isPropertyAlarmInstalled,
      typeof: 'boolean',
    },

    propertyMaindorLock: {
      required: true,
      in: {
        items: constants.HOME_PROPERTY_MAINDOR_LOCK,
      },
    },

    propertyPatiodorLock: {
      required: true,
      in: {
        items: constants.HOME_PROPERTY_PATIODOR_LOCK,
      },
    },

    propertyExternaldorLock: {
      required: true,
      in: {
        items: constants.HOME_PROPERTY_EXTERNALDOR_LOCK,
      },
    },

    propertyWindowLock: {
      required: true,
      typeof: 'boolean',
    },

    propertyTotalSmokeAlarms: {
      required: true,
      in: {
        items: constants.HOME_PROPERTY_TOTAL_SMOKE_ALARMS,
      },
    },

    propertyLocalWatch: {
      required: true,
      in: {
        items: constants.HOME_PROPERTY_LOCAL_WATCH,
      },
    },
  };
};

export const initInsuredRules = (options) => (insured = {}, data) => {
  return {
    title: {
      in: {
        items: constants.INSURED_TITLE,
      },
    },
    firstName: {
      name: [2, 50],
      regex: nameRegex,
    },
    surname: {
      name: [2, 50],
      regex: nameRegex,
    },
    dateOfBirth: {
      date_format: DATE_FORMAT,
      dateOfBirth: true,
    },

    // TODO check condition
    postcodeContact: {
      postcode: true,
    },
    houseNumberOrNameContact: {
      typeof: 'string',
    },

    maritalStatus: {
      required: true,
      in: {
        items: options.maritalStatusOptions,
      },
    },
    employmentStatus: {
      required: true,
      in: {
        items: constants.INSURED_EMPLOYMENT_STATUS,
      },
    },

    // occupation
    occupationList: {
      required: constants.INSURED_EMPLOYMENT_STATUS_BUSINESS.includes(insured.employmentStatus),
      alphaNum: true,
      length: 3,
    },
    occupationKey: {
      required: constants.INSURED_EMPLOYMENT_STATUS_BUSINESS.includes(insured.employmentStatus),
      typeof: 'string',
    },
    businessList: {
      required: constants.INSURED_EMPLOYMENT_STATUS_BUSINESS.includes(insured.employmentStatus),
      alphaNum: true,
      length: 3,
    },
    businessKey: {
      required: constants.INSURED_EMPLOYMENT_STATUS_BUSINESS.includes(insured.employmentStatus),
      typeof: 'string',
    },
    partTimeOccupation: {
      required: true,
      typeof: 'boolean',
    },
    occupationListPt: {
      required: insured.partTimeOccupation,
      alphaNum: true,
      length: 3,
    },
    occupationKeyPt: {
      required: insured.partTimeOccupation,
    },
    businessListPt: {
      required: insured.partTimeOccupation,
    },
    businessKeyPt: {
      required: insured.partTimeOccupation,
    },

    telephone: {
      telephone: [10, 12],
    },
    email: {
      email: true,
      required: true,
    },

    jointInsured: {
      required: true,
      typeof: 'boolean',
    },

    propertyBankrupt: {
      required: true,
      typeof: 'boolean',
    },

    specialTermsPolicy: {
      required: true,
      typeof: 'boolean',
    },

    policyDeclined: {
      required: true,
      typeof: 'boolean',
    },

    occupantsConvictions: {
      required: true,
      in: {
        items: constants.COVER_OCCUPANTS_CONVICTIONS,
      },
    },

    propertyInsuranceClaims: {
      required: true,
      typeof: 'boolean',
    },

    claims: {
      required: insured.propertyInsuranceClaims,
    },

    dateOfInsuranceStart: {
      date_format: DATE_FORMAT,
      after: {
        equal: true,
        compare: 'today',
      },
      before: {
        equal: true,
        compare: new UtcDate().addDays(constants.DATE_OF_INSURANCE_START_MAX_DAYS).format(),
      },
    },

    propertyPaymentType: {
      required: true,
      in: {
        items: constants.COVER_PROPERTY_PAYMENT_TYPE,
      },
    },

    buildNoClaimsBonus: {
      required: options.isBuildingCovered,
      switchedBy: {
        condition: options.isBuildingCovered,
      },
      in: {
        items: constants.COVER_BUILDING_NO_CLAIMS_BONUS,
      },
    },

    contentsNoClaimsBonus: {
      required: options.isContentsCovered,
      switchedBy: {
        condition: options.isContentsCovered,
      },
      in: {
        items: constants.COVER_CONTENTS_NO_CLAIMS_BONUS,
      },
    },

    buildVoluntaryExcess: {
      required: options.isBuildingCovered,
      switchedBy: {
        condition: options.isBuildingCovered,
      },
      in: {
        items: constants.COVER_VOLUNTARY_EXCESS,
      },
    },
    contentsVoluntaryExcess: {
      required: options.isContentsCovered,
      switchedBy: {
        condition: options.isContentsCovered,
      },
      in: {
        items: constants.COVER_VOLUNTARY_EXCESS,
      },
    },

    buildAccDam: {
      required: options.isBuildingCovered,
      switchedBy: {
        condition: options.isBuildingCovered,
      },
      typeof: 'boolean',
    },

    contentsAccDam: {
      required: options.isContentsCovered,
      switchedBy: {
        condition: options.isContentsCovered,
      },
      typeof: 'boolean',
    },

    // marketing

    outbound: {
      required: true,
      typeof: {
        typecheck: 'boolean',
      },
    },
    userKeepSignIn: {
      required: !options.config.hiddenQuestions['user-keep-me-sign-in'],
      typeof: 'boolean',
    },
    directMarketingEmail: {
      typeof: {
        typecheck: 'boolean',
      },
    },
    directMarketingPhone: {
      typeof: {
        typecheck: 'boolean',
      },
    },
    directMarketingPost: {
      typeof: {
        typecheck: 'boolean',
      },
    },
    directMarketingSms: {
      typeof: {
        typecheck: 'boolean',
      },
    },
  };
};

export const getClaimsRules = claim => ({
  claimDate: {
    date_format: DATE_FORMAT,
    before: {
      compare: 'today',
      equal: true,
    },
    after: {
      compare: '-5 years',
    },
  },

  propertyClaimType: {
    required: true,
    in: {
      items: constants.CLAIM_PROPERTY_CLAIM_TYPE,
    },
  },

  claimCause: {
    required: true,
    in: {
      items: constants.CLAIM_CAUSE,
    },
  },

  claimStatus: {
    required: true,
    in: {
      items: constants.CLAIM_STATUS,
    },
  },

  claimAmount: (() => {
    if (claim.claimStatus === 'NC') return {};

    return {
      is_not: { other: null },
      integer: true,
      claimAmount: true,
    };
  })(),

  claimLocation: {
    required: true,
    typeof: 'boolean',
  },
});

export const getBicycleLaptopRules = bicycleLaptop => ({
  propertyBicycleOrLaptop: {
    required: true,
    in: {
      items: constants.COVER_PROPERTY_BICYCLE_OR_LAPTOP,
    },
  },
  propertyBicycleLaptopDescription: {
    required: true,
    regex: descriptionRegex,
  },
  propertyBicycleLaptopValue: {
    is_not: { other: null },
    integer: true,
  },
  propertyBicycleLaptopOutside: {
    required: true,
    typeof: 'boolean',
  },
});

export const getSpecificItemRules = specificItem => ({
  propertySpecificItemsItem: {
    in: {
      items: constants.COVER_PROPERTY_SPECIFIC_ITEMS,
    },
  },
  propertySpecificItemsDescription: {
    required: true,
    regex: descriptionRegex,
  },
  propertySpecificItemsValue: {
    is_not: { other: null },
    integer: true,
  },
  propertySpecificItemsOutside: {
    required: true,
    typeof: 'boolean',
  },
});

export const getAdditionalInsuredRules = ({ additionalEmploymentStatus, additionalPartTimeOccupation }) => {
  const isEmploymentStatusRequireBusinessType =
    constants.INSURED_EMPLOYMENT_STATUS_BUSINESS.includes(additionalEmploymentStatus);
  const isFullTimeEducation = additionalEmploymentStatus === constants.INSURED_EMPLOYMENT_STATUS_EDUCATION;

  return {
    additionalTitle: {
      in: {
        items: constants.INSURED_TITLE,
      },
    },
    additionalFirstName: {
      name: [2, 50],
      regex: nameRegex,
    },
    additionalSurname: {
      name: [2, 50],
      regex: nameRegex,
    },
    additionalDateOfBirth: {
      date_format: DATE_FORMAT,
      dateOfBirth: true,
    },
    additionalMaritalStatus: {
      required: true,
      in: {
        items: constants.MARITAL_STATUS,
      },
    },
    additionalRelationshipProposer: {
      required: true,
      in: {
        items: constants.INSURED_ADDITIONAL_RELATIONSHIP_PROPOSER,
      },
    },
    additionalEmploymentStatus: {
      required: true,
      in: {
        items: constants.INSURED_EMPLOYMENT_STATUS,
      },
    },
    additionalOccupationKey: {
      required: isEmploymentStatusRequireBusinessType,
      typeof: 'string',
    },
    additionalOccupationList: {
      required: isEmploymentStatusRequireBusinessType,
      alphaNum: true,
      length: 3,
    },
    additionalBusinessKey: {
      required: isEmploymentStatusRequireBusinessType &&
        !isFullTimeEducation,
      typeof: 'string',
    },
    additionalBusinessList: {
      required: isEmploymentStatusRequireBusinessType &&
        !isFullTimeEducation,
      alphaNum: true,
      length: 3,
    },
    additionalPartTimeOccupation: {
      required: true,
      typeof: 'boolean',
    },
    additionalOccupationKeyPt: {
      required: additionalPartTimeOccupation,
    },
    additionalOccupationListPt: {
      required: additionalPartTimeOccupation,
      alphaNum: true,
      length: 3,
    },
    additionalBusinessKeyPt: {
      required: additionalPartTimeOccupation,
    },
    additionalBusinessListPt: {
      required: additionalPartTimeOccupation,
    },
  };
};

export const createHomeRules = (data, config) => {
  const validationHelpers = {};

  validationHelpers.isBuildingCovered = true;
  validationHelpers.isContentsCovered = true;
  validationHelpers.isPropertyOwned = true;
  validationHelpers.isPropertyPurchased = true;
  validationHelpers.isPropertyAlarmInstalled = false;

  if (data.home) {
    const {
      propertyCoverType = null,
      propertyOwned = null,
      propertyYear = null,
    } = data.home;

    validationHelpers.isBuildingCovered = propertyCoverType === 'BC' || propertyCoverType === 'B';
    validationHelpers.isContentsCovered = propertyCoverType === 'BC' || propertyCoverType === 'C';
    validationHelpers.isPropertyOwned = propertyOwned === 'YM' || propertyOwned === 'YO';
    validationHelpers.propertyYearsLivedMax = (() => {
      if (!propertyYear) return constants.PROPERTY_YEARS_LIVED_MAX;
      return new UtcDate().getFullYear() - Number(propertyYear);
    })();
  }

  if (data.content) {
    const {
      propertyPurchased = null,
      propertyAlarmInstalled = null,
    } = data.content;

    validationHelpers.isPropertyPurchased = propertyPurchased !== 'N';
    validationHelpers.isPropertyAlarmInstalled = propertyAlarmInstalled;

    // contents total
    validationHelpers.totalContentsCost = calcContentsSum(data.content);
  }

  if (data.insured?.[0]) {
    validationHelpers.maritalStatusOptions = constants.MARITAL_STATUS;
  }

  validationHelpers.config = config;

  return {
    getHomeRules: initHomeRules(validationHelpers),
    getContentRules: initContentRules(validationHelpers),
    getInsuredRules: initInsuredRules(validationHelpers),
    getAdditionalInsuredRules,
    getBicycleLaptopRules,
    getSpecificItemRules,
    getClaimsRules,
  };
};

export const prevalidate = async (dataRaw, config) => {
  const data = cloneDeep(dataRaw);
  const rules = createHomeRules(data, config);

  await Promise.all(
    [
      'home',
      'content',
      'insured',
    ].map(async stepId => {
      if (!data[stepId]) return;

      if (isObjectLike(data[stepId]) && stepId !== 'insured') {
        const stepTemplate = homePayloadTemplates[stepId.toUpperCase()];
        const stepFieldsSorted = Object.keys(stepTemplate).sort();
        const stepRules = rules[`get${capitalize(stepId)}Rules`](data[stepId], data);

        const stepValidity = await Promise.all(stepFieldsSorted.map(field => {
          return validate(data[stepId][field], stepRules[field]);
        }));

        stepFieldsSorted.forEach((field, i) => {
          if (!stepValidity[i].valid) {
            data[stepId][field] = stepTemplate[field];
          }
        });

        if (stepId !== 'content') return;

        const isContentsCovered = data?.home.propertyCoverType === 'BC' || data?.home.propertyCoverType === 'C';

        // reset bicycle/laptop & specificItems if contents not covered
        if (!isContentsCovered) {
          data[stepId].bicyclesOrLaptopsDetails = [];
          data[stepId].propertyLaptopInsurance = false;

          data[stepId].specificItemsDetails = [];
          data[stepId].propertyItemsInsurance = false;

          return;
        }

        // bicycle/laptop
        if (data[stepId].propertyLaptopInsurance === true && Array.isArray(data[stepId].bicyclesOrLaptopsDetails) && data[stepId].bicyclesOrLaptopsDetails.length > 0) {
          const bicycleLaptopTemplate = homePayloadTemplates.BICYCLE_OR_LAPTOP_DETAIL;
          const bicycleLaptopSorted = Object.keys(bicycleLaptopTemplate).sort();

          await Promise.all(data[stepId].bicyclesOrLaptopsDetails.map(async (item, index) => {
            const bicycleLaptopRules = rules.getBicycleLaptopRules(item);
            const bicycleLaptopValidity = await Promise.all(bicycleLaptopSorted.map(field => validate(item[field], bicycleLaptopRules[field])));

            bicycleLaptopSorted.forEach((field, i) => {
              if (!bicycleLaptopValidity[i].valid) {
                data[stepId].bicyclesOrLaptopsDetails[index][field] = bicycleLaptopTemplate[field];
              }
            });
            return bicycleLaptopValidity;
          }));
        } else {
          data[stepId].bicyclesOrLaptopsDetails = [];
          data[stepId].propertyLaptopInsurance = false;
        }

        // specific item
        if (data[stepId].propertyItemsInsurance === true && Array.isArray(data[stepId].specificItemsDetails) && data[stepId].specificItemsDetails.length > 0) {
          const specificItemTemplate = homePayloadTemplates.SPECIFIC_ITEM_DETAIL;
          const specificItemSorted = Object.keys(specificItemTemplate).sort();

          await Promise.all(data[stepId].specificItemsDetails.map(async (item, index) => {
            const specificItemRules = rules.getSpecificItemRules(item);
            const specificItemValidity = await Promise.all(specificItemSorted.map(field => validate(item[field], specificItemRules[field])));

            specificItemSorted.forEach((field, i) => {
              if (!specificItemValidity[i].valid) {
                data[stepId].specificItemsDetails[index][field] = specificItemTemplate[field];
              }
            });
            return specificItemValidity;
          }));
        } else {
          data[stepId].specificItemsDetails = [];
          data[stepId].propertyItemsInsurance = false;
        }
      }

      if (Array.isArray(data[stepId]) && stepId === 'insured') {
        const insured = data[stepId][0];
        const insuredTemplate = homePayloadTemplates.INSURED;
        const insuredFieldsSorted = Object.keys(insuredTemplate).sort();
        const insuredRules = rules.getInsuredRules(insured);

        const insuredValidity = await Promise.all(insuredFieldsSorted.map(field => {
          return validate(insured[field], insuredRules[field]);
        }));

        insuredFieldsSorted.forEach((field, i) => {
          if (!insuredValidity[i].valid) {
            insured[field] = insuredTemplate[field];
          }
        });

        // claims
        if (insured.propertyInsuranceClaims === true && Array.isArray(insured.claims) && insured.claims.length > 0) {
          const claimTemplate = homePayloadTemplates.CLAIM;
          const claimFieldsSorted = Object.keys(claimTemplate).sort();

          await Promise.all(insured.claims.map(async (claim, claimIndex) => {
            const claimRules = rules.getClaimsRules(claim);
            const claimValidity = await Promise.all(claimFieldsSorted.map(field => validate(claim[field], claimRules[field])));

            claimFieldsSorted.forEach((field, i) => {
              if (!claimValidity[i].valid) {
                insured.claims[claimIndex][field] = claimTemplate[field];
              }
            });
            return claimValidity;
          }));

          insured.claims = insured.claims.filter(claim => claim.claimDate !== null);
          if (insured.claims.length === 0) insured.propertyInsuranceClaims = false;
        } else {
          insured.claims = [];
        }

        // Additional insured
        const additionalInsured = data[stepId][1];

        if (insured.jointInsured === false) {
          if (additionalInsured) {
            data[stepId].splice(1, 1);
          }

          return;
        }

        if (insured.jointInsured === true && !additionalInsured) {
          insured.jointInsured = false;
          return;
        }

        const addInsuredTemplate = homePayloadTemplates.INSURED_ADDITIONAL;
        const addInsuredFieldsSorted = Object.keys(addInsuredTemplate).sort();
        const addInsuredRules = rules.getAdditionalInsuredRules(additionalInsured);

        const addInsuredValidity = await Promise.all(addInsuredFieldsSorted.map(field => {
          return validate(additionalInsured[field], addInsuredRules[field]);
        }));

        addInsuredFieldsSorted.forEach((field, i) => {
          if (!addInsuredValidity[i].valid) {
            additionalInsured[field] = addInsuredTemplate[field];
          }
        });
      }
    }),
  );

  return data;
};
